/**
 * NCB slabs edited alongside their product (COUNTRY-8).
 *
 * The diff below decides DELETEs, so it lives here as a pure function rather
 * than inline in the modal: getting it wrong destroys a product's slabs, and
 * that is not something to find out by clicking.
 *
 * `id` is present only on rows that already exist server-side. That is the
 * whole discriminator — a row without one has never been saved, a row whose id
 * has vanished from the on-screen list has been removed by the user.
 */

export interface NcbSlabDraft {
  id?: string;
  claim_free_years: string;
  ncb_percentage: string;
  vehicle_category: string;
}

/** Codes as `NCBSlab.vehicle_category` documents them — NOT the wizard's. */
export const NCB_VEHICLE_CATEGORIES = [
  { value: '', label: 'All vehicle types' },
  { value: '2w', label: 'Two Wheeler' },
  { value: '4w', label: 'Four Wheeler' },
  { value: 'cv', label: 'Commercial Vehicle' },
];

export const emptyNcbSlab = (): NcbSlabDraft => ({
  claim_free_years: '',
  ncb_percentage: '',
  vehicle_category: '',
});

/**
 * A row the user actually filled in.
 *
 * Both numbers are required by the model, so a half-typed row is ignored
 * rather than rejected — clicking "Add NCB slab" and then saving without
 * filling it in should not block the product save with a validation error
 * about a row the user had already abandoned.
 */
export const ncbSlabFilled = (slab: NcbSlabDraft) =>
  slab.claim_free_years.trim() !== '' && slab.ncb_percentage.trim() !== '';

export const ncbSlabsEqual = (a: NcbSlabDraft, b: NcbSlabDraft) =>
  a.claim_free_years === b.claim_free_years &&
  a.ncb_percentage === b.ncb_percentage &&
  a.vehicle_category === b.vehicle_category;

export interface NcbSlabDiff {
  removed: NcbSlabDraft[];
  created: NcbSlabDraft[];
  updated: NcbSlabDraft[];
}

/**
 * What to send so the server's slabs match the screen.
 *
 * `original` is the server's copy as loaded; `current` is what is on screen.
 * Unchanged rows appear in none of the three buckets — an untouched slab is
 * left alone rather than rewritten, so `updated_at` keeps meaning something.
 */
export function ncbSlabDiff(current: NcbSlabDraft[], original: NcbSlabDraft[]): NcbSlabDiff {
  const kept = current.filter(ncbSlabFilled);
  const keptIds = new Set(kept.map((s) => s.id).filter(Boolean) as string[]);

  return {
    // A row that was emptied out counts as removed: it is no longer `kept`, so
    // its id is absent from keptIds and it lands here rather than silently
    // surviving on the server.
    removed: original.filter((s) => s.id && !keptIds.has(s.id)),
    created: kept.filter((s) => !s.id),
    updated: kept.filter((s) => {
      if (!s.id) return false;
      const before = original.find((o) => o.id === s.id);
      return !!before && !ncbSlabsEqual(before, s);
    }),
  };
}
